import {
  ExportDropdown,
  DeleteConfirmation,
  ExportDialog,
  SearchInput,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
  RouteLink,
} from '~/components';
import { useApi, useConfirmation, useIntegrations, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { mimeTypes, QueryString } from '~/utils';
import { ActiveStatusSelect } from '../custom-data/components';
import OpportunityPipelineDrawer from './OpportunityPipelineDrawer';
import OpportunityPipelineViewDrawer from './OpportunityPipelineViewDrawer';
import HubspotIndicator from '../../pipeline/components/HubspotIndicator';
import SalesforceIndicator from '../../pipeline/components/SalesforceIndicator';

function OpportunityPipelinePage() {
  const documentTitle = useDocumentTitle('Opportunity Pipelines');
  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();
  const integrations = useIntegrations();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true' });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .opportunityPipelines()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .opportunityPipelines()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (opportunityPipeline) => {
    setDialog(
      <OpportunityPipelineDrawer
        opportunityPipeline={opportunityPipeline}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Opportunity Pipelines');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  const handleView = async (opportunityPipeline) => {
    setDialog(
      <OpportunityPipelineViewDrawer
        opportunityPipeline={opportunityPipeline}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Opportunity Pipelines');
        }}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).opportunityPipelines(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(opportunityPipeline) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>
        Are you sure you want to delete this Opportunity Pipeline?
      </DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .opportunityPipelines(opportunityPipeline ? opportunityPipeline.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;
  const integrationEnabled = integrations?.hubspot || integrations?.salesforce;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Opportunity Pipelines</Page.Title>
          <Page.Description>
            Opportunity pipelines are used to track different sales processes for opportunities.
          </Page.Description>
        </Page.Info>
        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('opportunity_pipelines.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('opportunity_pipelines.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>
          <IconButton icon="plus" tooltip="New Opportunity Pipeline" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />
          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column>Name</ListView.Column>
            <ListView.Column>Description</ListView.Column>
            <ListView.Column align="right">Associations</ListView.Column>
            {integrationEnabled && (
              <ListView.Column width="4rem" align="left">
                {integrations?.salesforce && <SalesforceIndicator variant="grey" />}
                {integrations?.hubspot && <HubspotIndicator variant="grey" />}
              </ListView.Column>
            )}
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((opportunityPipeline) => {
              const { name, isActive, id, description, isDefault, hubspotId, salesforceId, usedIn } =
                opportunityPipeline;
              const synced = hubspotId || salesforceId;
              const readOnly = synced;

              return (
                <ListView.Row
                  onClick={() => (readOnly ? handleView(opportunityPipeline) : handleEdit(opportunityPipeline))}
                  key={id}
                  isDisabled={!isActive}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListView.Cell>{description}</ListView.Cell>
                  <ListView.Cell>
                    <RouteLink
                      to={`/app/${workspace.key}/pipeline/opportunities?${new QueryString({
                        opportunityPipelineId: id,
                        opportunityStageCategoryId: 'all',
                      }).toString()}`}>
                      {usedIn}
                    </RouteLink>
                  </ListView.Cell>
                  {integrationEnabled && (
                    <ListView.Cell>
                      {salesforceId && <SalesforceIndicator />}
                      {hubspotId && <HubspotIndicator />}
                    </ListView.Cell>
                  )}

                  <ListViewActions>
                    {synced ? (
                      <ListViewActions.View onClick={() => handleView(opportunityPipeline)} />
                    ) : (
                      <>
                        <ListViewActions.Edit onClick={() => handleEdit(opportunityPipeline)} />
                        <hr />
                        <ListViewMenu>
                          {({ setIsOpen }) => (
                            <>
                              <ListViewMenu.Item onClick={() => handleEdit(opportunityPipeline)}>
                                Edit
                              </ListViewMenu.Item>
                              <ListViewMenu.Item
                                onMount={
                                  isActive
                                    ? async (setState) => {
                                        const { data } = await api.www
                                          .workspaces(workspace.id)
                                          .opportunityPipelines(id)
                                          .hasActiveStages();
                                        if (data) {
                                          setState({
                                            disabled: true,
                                            tooltip: 'A pipeline with active stages cannot be deactivated.',
                                          });
                                        }
                                      }
                                    : undefined
                                }
                                onClick={async () => {
                                  await handleActiveStatusChange(opportunityPipeline, !isActive);
                                  setIsOpen(false);
                                }}>
                                {isActive ? 'Deactivate' : 'Activate'}
                              </ListViewMenu.Item>
                              <ListViewMenu.DeleteItem
                                disabled={isDefault}
                                tooltip={
                                  isDefault
                                    ? 'The default pipeline cannot be deleted.'
                                    : 'A pipeline in use by opportunities or stages cannot be deleted.'
                                }
                                onCheckDependencies={
                                  isDefault
                                    ? undefined
                                    : async (workspace) =>
                                        (await workspace.opportunityPipelines(id).hasDependencies()).data
                                }
                                onClick={() => handleDelete(opportunityPipeline)}>
                                Delete
                              </ListViewMenu.DeleteItem>
                            </>
                          )}
                        </ListViewMenu>
                      </>
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Opportunity Pipelines" />
        </ListView>
      </Page.ListView>
      {dialog}
    </>
  );
}

export default OpportunityPipelinePage;
